// web/src/pages/external/ExternalTermsAcceptPage.tsx
import { useRef, useState } from "react";
import { useParams } from "react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { externalApi, type AcceptTermsExternalRequest } from "@/lib/external";
import { SignaturePad, type SignaturePadHandle } from "@/components/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ExternalTermsAcceptPage() {
  const { token = "" } = useParams();
  const sigRef = useRef<SignaturePadHandle>(null);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [doneMsg, setDoneMsg] = useState<string | null>(null);

  const { data: view, isLoading, error } = useQuery({
    queryKey: ["external-terms", token], queryFn: () => externalApi.getTerms(token), retry: false,
  });

  const acceptMut = useMutation({
    mutationFn: (body: AcceptTermsExternalRequest) => externalApi.acceptTerms(token, body),
    onSuccess: () => setDoneMsg("Thank you. We've recorded your acceptance."),
    onError: (e: Error) => setDoneMsg(e.message),
  });

  if (isLoading) return <p className="p-8 text-center">Loading…</p>;
  if (error)
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-semibold mb-2">Link not available</h1>
        <p className="text-muted-foreground">{(error as Error).message}</p>
      </div>
    );
  if (doneMsg) return <p className="p-8 text-center">{doneMsg}</p>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <header className="border-b pb-3">
        <h1 className="text-xl font-semibold">{view!.companyName}</h1>
        <p className="text-sm text-muted-foreground">Account {view!.accountReferenceNo} — Terms v{view!.versionNumber}</p>
      </header>
      <article className="prose max-w-none whitespace-pre-wrap">{view!.templateText}</article>

      <form
        className="space-y-3 border-t pt-4"
        onSubmit={(e) => {
          e.preventDefault();
          acceptMut.mutate({
            acceptorName: name,
            position: position || null,
            signatureBase64Png: sigRef.current?.toBase64Png() ?? null,
          });
        }}
      >
        <div><Label htmlFor="ex-name">Your name</Label><Input id="ex-name" required value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><Label htmlFor="ex-pos">Position</Label><Input id="ex-pos" value={position} onChange={(e) => setPosition(e.target.value)} /></div>
        <div><Label>Signature (optional)</Label><SignaturePad ref={sigRef} /></div>
        <div className="flex gap-2">
          <Button type="submit" disabled={!name || acceptMut.isPending}>{acceptMut.isPending ? "Sending…" : "I accept"}</Button>
          <Button type="button" variant="outline" onClick={() => window.location.href = "mailto:finance@allmark.com.au?subject=Terms%20questions"}>I have questions</Button>
        </div>
      </form>
    </div>
  );
}
